import {
  advanceHideout,
  buildProblem,
  craftProblem,
  FUEL_DEF_ID,
  FUEL_TANK_CAP,
  GENERATOR_MODULE_ID,
  generatorBurnPerSec,
  getHideoutModule,
  getItemDef,
  GYM_MODULE_ID,
  HIDEOUT_CRAFTS,
  HIDEOUT_MODULES,
  isGeneratorRunning,
  moduleLevel,
  productionFor,
  TRAINABLE_SKILLS,
  trainingCooldownLeftSec,
  trainingGain,
  trainProblem,
  type CraftDef,
  type GridState,
  type HideoutModuleDef,
  type HideoutState,
  type ProgressState,
  type SkillId,
} from "@rietz/shared";
import { skillViews } from "./profileLogic.js";

/**
 * The hideout screen's arithmetic, with no React in it — same split as
 * `questView.ts`, worth it here because of the CLOCK.
 *
 * The hideout ticks locally: `advanceHideout` is pure and shared, so the
 * client re-runs the server's own function against its guess at the server's
 * clock (`projectHideout`): server-now ≈ `lastAdvanceMs` + (perf-now − the
 * instant the profile arrived). No offset to estimate; every fresh profile
 * re-anchors it. Elapsed is clamped at zero here and again in
 * `advanceHideout` — a hideout never runs backwards.
 *
 * Materials come from the STASH only — what the server charges against — so
 * the screen never offers a Build the server refuses for `items`.
 * `trainingRows` costs nothing but the same projected clock, which is why the
 * gym's countdown lives here too.
 */

// ---------------------------------------------------------------------------
// The clock
// ---------------------------------------------------------------------------

/**
 * The hideout as the SERVER would describe it now, from the one it sent.
 * `profileAtMs` and `nowPerfMs` are both `performance.now()` readings;
 * `h.lastAdvanceMs` supplies the absolute instant.
 */
export function projectHideout(
  h: HideoutState,
  profileAtMs: number,
  nowPerfMs: number,
): HideoutState {
  const elapsedMs = nowPerfMs - profileAtMs;
  return advanceHideout(h, h.lastAdvanceMs + (elapsedMs > 0 ? elapsedMs : 0));
}

// ---------------------------------------------------------------------------
// Materials
// ---------------------------------------------------------------------------

/**
 * How many of each def lie in the stash, stacks summed — the shape
 * `buildProblem`/`craftProblem` take. Fitted attachments are not counted.
 */
export function stashCountsOf(stash: GridState): Map<string, number> {
  const counts = new Map<string, number>();
  for (const placed of stash.items) {
    const n = placed.item.stack;
    if (!Number.isFinite(n) || n <= 0) continue;
    counts.set(placed.item.defId, (counts.get(placed.item.defId) ?? 0) + Math.floor(n));
  }
  return counts;
}

/**
 * The first fuel canister's iid in the stash, or `null`. Placement order, so
 * "Add fuel" twice feeds two canisters.
 */
export function firstFuelIid(stash: GridState): string | null {
  for (const placed of stash.items) {
    if (placed.item.defId === FUEL_DEF_ID) return placed.item.iid;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Modules
// ---------------------------------------------------------------------------

/** One line of a cost list: what is wanted, what is there, and the colour. */
export interface NeedView {
  defId: string;
  /** The item's display name, or its raw id when content no longer has one. */
  name: string;
  need: number;
  have: number;
  ok: boolean;
}

/** "Lagerregal level 1" as a requirement, and whether it is met. */
export interface PrereqView {
  name: string;
  level: number;
  ok: boolean;
}

/** Everything about the rung the player is standing under. */
export interface NextLevelView {
  /** The level this build would REACH — `level + 1`. */
  level: number;
  blurb: string;
  buildSec: number;
  euros: number;
  /** Whether the euros alone are covered. Colours the price, nothing more. */
  enough: boolean;
  items: NeedView[];
  prereqs: PrereqView[];
  /** `buildProblem`'s verdict — `null` means the Build button is live. */
  problem: string | null;
}

/** One room, as the list on the left and the card on the right both read it. */
export interface ModuleView {
  def: HideoutModuleDef;
  level: number;
  maxLevel: number;
  /** Present only while this module is building. */
  building: { secLeft: number } | null;
  /** `null` at the top of the ladder. */
  next: NextLevelView | null;
}

/**
 * Every module in `HIDEOUT_MODULES` (display) order. `problem` comes from the
 * shared `buildProblem`, so the disabled button and the server's refusal are
 * one decision.
 */
export function moduleViews(
  h: HideoutState,
  euros: number,
  counts: ReadonlyMap<string, number>,
  nowMs: number,
): ModuleView[] {
  return HIDEOUT_MODULES.map((def) => {
    const level = moduleLevel(h, def.id);
    const state = ownModule(h, def.id);
    const ends = state?.buildEndsAtMs;
    const building =
      ends === undefined || !Number.isFinite(ends)
        ? null
        : { secLeft: Math.max(0, (ends - nowMs) / 1000) };

    const rung = def.levels[level];
    const next: NextLevelView | null =
      rung === undefined
        ? null
        : {
            level: level + 1,
            blurb: rung.blurb,
            buildSec: rung.buildSec,
            euros: rung.euros,
            enough: euros >= rung.euros,
            items: rung.items.map((cost) => need(counts, cost.defId, cost.count)),
            prereqs: rung.requires.map((req) => ({
              name: getHideoutModule(req.moduleId)?.name ?? req.moduleId,
              level: req.level,
              ok: moduleLevel(h, req.moduleId) >= req.level,
            })),
            problem: buildProblem(h, euros, counts, def.id),
          };

    return { def, level, maxLevel: def.levels.length, building, next };
  });
}

// ---------------------------------------------------------------------------
// Crafting
// ---------------------------------------------------------------------------

/** One recipe card. */
export interface RecipeView {
  def: CraftDef;
  outputName: string;
  outputCount: number;
  inputs: NeedView[];
  /** `craftSec` in whole minutes, which is what the cards print. */
  minutes: number;
  /** `craftProblem`'s verdict — `null` means the Start button is live. */
  problem: string | null;
}

/** The job on the bench, if there is one. */
export interface ActiveCraftView {
  def: CraftDef;
  outputName: string;
  outputCount: number;
  /** 0..1, for the bar. */
  progress01: number;
  /** Wall clock left AT THE CURRENT RATE — see the note below. */
  secLeft: number;
  done: boolean;
}

export interface CraftsView {
  recipes: RecipeView[];
  active: ActiveCraftView | null;
}

/**
 * The recipe cards for one module and whatever is running in it. EVERY recipe
 * is listed, including ones the level cannot run — "needs level 2" is why a
 * player upgrades. `secLeft` for a powered craft is what is left IF the power
 * stays on.
 */
export function craftViews(
  h: HideoutState,
  counts: ReadonlyMap<string, number>,
  moduleId: string,
): CraftsView {
  const recipes: RecipeView[] = [];
  for (const def of HIDEOUT_CRAFTS) {
    if (def.moduleId !== moduleId) continue;
    recipes.push({
      def,
      outputName: itemName(def.output.defId),
      outputCount: def.output.count,
      inputs: def.inputs.map((input) => need(counts, input.defId, input.count)),
      minutes: Math.round(def.craftSec / 60),
      problem: craftProblem(h, counts, def.id),
    });
  }

  let active: ActiveCraftView | null = null;
  for (const running of h.crafts) {
    const def = HIDEOUT_CRAFTS.find((c) => c.id === running.craftId);
    if (def === undefined || def.moduleId !== moduleId) continue;
    const progress = Math.max(0, Math.min(def.craftSec, running.progressSec));
    active = {
      def,
      outputName: itemName(def.output.defId),
      outputCount: def.output.count,
      progress01: def.craftSec > 0 ? progress / def.craftSec : 1,
      secLeft: def.craftSec - progress,
      done: progress >= def.craftSec,
    };
    break;
  }

  return { recipes, active };
}

// ---------------------------------------------------------------------------
// The generator and the hive
// ---------------------------------------------------------------------------

/** The fuel gauge, whole. */
export interface GeneratorView {
  level: number;
  /** The switch's position — which is not the same as running. */
  on: boolean;
  /** Switched on, built, and with fuel left: the condition power accrues under. */
  running: boolean;
  fuelUnits: number;
  tankCap: number;
  /** Minutes the tank covers at the CURRENT level's burn. `null` at level 0,
   *  where there is no generator to burn anything. */
  minutesLeft: number | null;
}

export function generatorView(h: HideoutState): GeneratorView {
  const level = moduleLevel(h, GENERATOR_MODULE_ID);
  const burnPerSec = generatorBurnPerSec(level);
  return {
    level,
    on: h.generatorOn,
    running: isGeneratorRunning(h),
    fuelUnits: h.fuelUnits,
    tankCap: FUEL_TANK_CAP,
    minutesLeft: burnPerSec > 0 ? h.fuelUnits / burnPerSec / 60 : null,
  };
}

/** A passive producer's buffer. */
export interface ProductionView {
  stored: number;
  cap: number;
  /** Seconds until the next whole unit — `null` at cap, where nothing is
   *  accruing at all and a countdown would be a lie. */
  secToNext: number | null;
}

/** `null` for a module that produces nothing at its current level. */
export function productionView(h: HideoutState, moduleId: string): ProductionView | null {
  const prod = productionFor(moduleId, moduleLevel(h, moduleId));
  if (prod === null) return null;

  const state = ownProduction(h, moduleId);
  const storedRaw = state === null || !Number.isFinite(state.stored) ? 0 : state.stored;
  const progress = state === null || !Number.isFinite(state.progressSec) ? 0 : state.progressSec;
  const stored = Math.max(0, Math.min(prod.cap, Math.floor(storedRaw)));

  return {
    stored,
    cap: prod.cap,
    secToNext: stored >= prod.cap ? null : Math.max(0, prod.periodSec - progress),
  };
}

// ---------------------------------------------------------------------------
// The gym
// ---------------------------------------------------------------------------

/**
 * Skill display names, DERIVED FROM `skillViews` (the one place the client
 * names a skill) rather than written a second time. Built once from a
 * progression of zeroes.
 */
const SKILL_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  skillViews({ endurance: 0, strength: 0, vitality: 0, gunsmith: 0 }).map((v) => [v.id, v.label]),
);

/** "Endurance". Falls back to the raw id, which can only happen for a skill a
 *  newer server knows about and this build does not. */
export function skillLabel(skill: SkillId): string {
  return SKILL_LABELS[skill] ?? skill;
}

/** One line of the gym card: a skill, where it stands, and whether the bench
 *  is free. */
export interface TrainingRow {
  skill: SkillId;
  label: string;
  /** The skill as the PROFILE has it — 0 while no profile has landed. */
  value: number;
  /**
   * What a PERFECT session pays at the gym's level. The half-session floor is
   * not shown.
   */
  gainPerSession: number;
  /** Seconds until this skill can be trained again; 0 the moment it can be. */
  cooldownLeftSec: number;
  /** `trainProblem`'s verdict — `null` means the button is live. */
  problem: string | null;
}

/**
 * The two trainable skills, in `TRAINABLE_SKILLS` order. `problem` and the
 * countdown come from the SHARED functions the server decides with. `progress`
 * is nullable — the overlay may be up before the first profile lands.
 */
export function trainingRows(
  h: HideoutState,
  progress: ProgressState | null,
  nowMs: number,
): TrainingRow[] {
  const gain = trainingGain(moduleLevel(h, GYM_MODULE_ID), 1);
  return TRAINABLE_SKILLS.map((skill) => ({
    skill,
    label: skillLabel(skill),
    value: skillValue(progress, skill),
    gainPerSession: gain,
    cooldownLeftSec: trainingCooldownLeftSec(h, skill, nowMs),
    problem: trainProblem(h, skill, nowMs),
  }));
}

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

/**
 * Every refusal code the shared problem functions and the server can answer
 * with, as one sentence — the same code arrives via the greyed button and via
 * an `invResult` toast.
 */
export function reasonLabel(reason: string): string {
  switch (reason) {
    case "unknown":
      return "This build does not know that one.";
    case "maxed":
      return "Already at its highest level.";
    case "busy":
      return "Something is already running here.";
    case "prereq":
      return "Another module has to be built first.";
    case "money":
      return "Not enough money.";
    case "items":
      return "Missing materials in the stash.";
    case "level":
      return "This module is not built far enough.";
    case "power":
      return "The generator is not running.";
    case "fuel":
      return "The tank is empty.";
    case "full":
      return "The tank is already full.";
    case "space":
      return "No room in the stash.";
    case "not-done":
      return "Not finished yet.";
    case "empty":
      return "Nothing to take.";
    case "not-fuel":
      return "That is not a fuel canister.";
    case "in-raid":
      return "Not while you are in a raid.";
    case "cooldown":
      return "Still recovering from the last session.";
    default:
      // A refusal from a newer server than this build. Saying something true
      // and useless beats printing a machine code at the player.
      return UNKNOWN_REASON;
  }
}

/** What `reasonLabel` says about a code it has never heard of. Exported so the
 *  test can assert that no code in the real vocabulary reaches it. */
export const UNKNOWN_REASON = "Not possible right now.";

// ---------------------------------------------------------------------------

/** One skill off a progression that may not have arrived, or may have arrived
 *  from a database blob. Never `NaN`, which would print as one. */
function skillValue(progress: ProgressState | null, skill: SkillId): number {
  const raw = progress?.skills[skill];
  return typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? raw : 0;
}

function need(counts: ReadonlyMap<string, number>, defId: string, count: number): NeedView {
  const have = counts.get(defId) ?? 0;
  return { defId, name: itemName(defId), need: count, have, ok: have >= count };
}

function itemName(defId: string): string {
  return getItemDef(defId)?.name ?? defId;
}

/** Own-property reads off the wire-supplied records, for the same reason
 *  `state.ts` guards its own: `modules["toString"]` must be nothing. */
function ownModule(h: HideoutState, id: string): HideoutState["modules"][string] | null {
  return Object.prototype.hasOwnProperty.call(h.modules, id) ? h.modules[id] ?? null : null;
}

function ownProduction(h: HideoutState, id: string): HideoutState["production"][string] | null {
  return Object.prototype.hasOwnProperty.call(h.production, id) ? h.production[id] ?? null : null;
}
